import { createStore, createEvent, createEffect, combine } from 'effector';
import IPost from '@interfaces/IPost';
import request from '@helpers/request';
import api from '@constants/endpoints';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from './pagination';
import createPreloadersStore from './preloaders';
import createFiltersStore from './filters';
import removeUndefinedFilters from '@helpers/removeUndefinedFilters';
import { block, updateItemHandler, unblock, hidePost, unhidePost } from './posts';
import localeWeight, { LOCALE } from '@stores/localeWeight';

interface ISpamCachedPage {
  page: number;
  pageSize: number;
  total: number;
  items: IPost[];
  cacheKey: string;
}

interface ISpamPrefetchRequest {
  page: number;
  pageSize: number;
  cacheKey: string;
  isLoading: boolean;
}

export const reset = createEvent('Spam store reset');
export const clearPrefetchedPage = createEvent('Clear prefetched spam posts page');
export const showPrefetchedPage = createEvent<IPost[]>('Show prefetched spam posts page');
export const resetFastPrefetch = createEvent('Reset fast spam posts prefetch');

export const DEFAULT_SPAM_FILTERS = {
  sort: 'createdDateTime,desc',
  needChecked: false,
};

const defaultFilters = { ...DEFAULT_SPAM_FILTERS };

const getLocaleValue = () =>
  localeWeight.getState() === LOCALE.ALL ? undefined : localeWeight.getState();

const buildCacheKey = ({
  filters,
  locale,
  pageSize,
}: {
  filters: Record<string, any>;
  locale?: string;
  pageSize?: number;
}) => JSON.stringify({ ...filters, locale, pageSize });

const getCurrentCacheKey = (pageSize?: number) =>
  buildCacheKey({
    filters: { ...defaultFilters, ...filters.getState() },
    locale: getLocaleValue(),
    pageSize,
  });

export const isCurrentPrefetchRequest = (
  requestData: ISpamPrefetchRequest | null,
  page: number,
  pageSize?: number,
  cacheKey?: string,
) =>
  !!requestData &&
  requestData.isLoading &&
  requestData.page === page &&
  requestData.pageSize === pageSize &&
  requestData.cacheKey === cacheKey;

export const getList = createEffect({
  name: 'getList',
  async handler(params?: { pagination?: PaginationProps; filters?: Record<string, any> }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };

    let filtersParams = filters.getState();
    if (params?.filters) {
      setFilters(params.filters);
      filtersParams = params.filters;
      paginationParams.current = 1;
    }

    const { current, pageSize } = paginationParams;
    const savedPageSize = Number(localStorage.getItem('spamPageSize'));
    const requestPageSize = params?.pagination?.pageSize || savedPageSize || pageSize;

    const response = await request(api.spam.getList, {
      params: {
        paged: true,
        size: requestPageSize,
        page: (current || 1) - 1,
        ...defaultFilters,
        ...removeUndefinedFilters({
          ...filtersParams,
          locale: localeWeight.getState() === LOCALE.ALL ? undefined : localeWeight.getState(),
        }),
      },
    });
    return response;
  },
});

export const prefetchPage = createEffect({
  name: 'prefetchSpamPostsPage',
  async handler(page: number) {
    const { pageSize } = pagination.getState();
    const filtersParams = filters.getState();
    const locale = getLocaleValue();

    const response = await request(api.spam.getList, {
      params: {
        paged: true,
        size: pageSize,
        page: page - 1,
        ...defaultFilters,
        ...removeUndefinedFilters({ ...filtersParams, locale }),
      },
    });

    return {
      page,
      pageSize: pageSize || 0,
      total: response.data.body.totalElements,
      items: response.data.body.content,
      cacheKey: buildCacheKey({
        filters: { ...defaultFilters, ...filtersParams },
        locale,
        pageSize,
      }),
    };
  },
});

export const getById = createEffect({
  name: 'getById',
  async handler(id: IPost['id']) {
    setFilters({ id });

    if (!id) {
      getList();
      return null;
    } else {
      const response = await request(api.spam.getList, { params: { postId: id } });
      if (!response.data.body) return null;
      return response;
    }
  },
});

const { pagination, setPagination } = createPaginationStore({
  getList,
  listName: 'spam',
  resetEvent: reset,
  bodyPath: ['data', 'body'],
});

export { setPagination };

const preloaders = createPreloadersStore([
  hidePost,
  unhidePost,
  getList,
  block,
  unblock,
  getById,
  prefetchPage,
]);

const { filters, setFilters } = createFiltersStore({
  listName: 'spam',
  deaultValues: defaultFilters,
  resetEvent: reset,
});

export { setFilters };

const prefetchedPageRequest = createStore<ISpamPrefetchRequest | null>(null)
  .on(prefetchPage, (_, page) => {
    const pageSize = pagination.getState().pageSize || 0;
    return {
      page,
      pageSize,
      cacheKey: getCurrentCacheKey(pageSize),
      isLoading: true,
    };
  })
  .on(prefetchPage.done, (state) => (state ? { ...state, isLoading: false } : null))
  .on(prefetchPage.fail, (state) => (state ? { ...state, isLoading: false } : null))
  .on(clearPrefetchedPage, () => null)
  .on(getList, () => null)
  .reset(reset);

const shouldPrefetchNextPageFast = createStore(false)
  .on(getList.done, () => true)
  .on(prefetchPage.done, () => false)
  .on(resetFastPrefetch, () => false)
  .on(clearPrefetchedPage, () => false)
  .reset(reset);

const isRelevantPrefetchedPage = (pageData: ISpamCachedPage | null) => {
  if (!pageData) return false;
  const { current, pageSize } = pagination.getState();
  return (
    pageData.page === (current || 1) + 1 &&
    pageData.pageSize === pageSize &&
    pageData.cacheKey === getCurrentCacheKey(pageSize) &&
    pageData.items.length > 0
  );
};

const prefetchedPage = createStore<ISpamCachedPage | null>(null)
  .on(prefetchPage.done, (_, { result }) => {
    if (
      result.pageSize !== pagination.getState().pageSize ||
      result.cacheKey !== getCurrentCacheKey(result.pageSize)
    ) {
      return null;
    }
    return result;
  })
  .on(clearPrefetchedPage, () => null)
  .on(getList, () => null)
  .on(block.done, consumePrefetchedItem)
  .on(unblock.done, consumePrefetchedItem)
  .on(hidePost.done, consumePrefetchedItem)
  .on(unhidePost.done, () => null)
  .reset(reset);

function consumePrefetchedItem(state: ISpamCachedPage | null) {
  if (!isRelevantPrefetchedPage(state) || !state) return null;
  return {
    ...state,
    total: Math.max(state.total - 1, 0),
    items: state.items.slice(1),
  };
}

const removeAndFillFromPrefetch = (state: IPost[], postId: IPost['id']) => {
  const nextState = state.filter((post) => post.id !== postId);
  const prefetched = prefetchedPage.getState();

  if (!prefetched || !isRelevantPrefetchedPage(prefetched)) {
    getList();
    return nextState;
  }

  const nextPost = prefetched.items[0];
  if (!nextPost || nextState.some((post) => post.id === nextPost.id)) return nextState;

  setPagination({
    ...pagination.getState(),
    total: Math.max((pagination.getState().total || 0) - 1, 0),
  });

  return [...nextState, nextPost].slice(0, pagination.getState().pageSize || nextState.length + 1);
};

const list = createStore<IPost[]>([])
  .on(getList.done, (state, { result }) => result.data.body.content)
  .on(showPrefetchedPage, (_, payload) => payload)
  .on(block.done, (state, { params }) => removeAndFillFromPrefetch(state, params.postId))
  .on(unblock.done, (state, { params }) => removeAndFillFromPrefetch(state, params.postId))
  .on(hidePost.done, (state, { params }) => removeAndFillFromPrefetch(state, params.postId))
  .on(unhidePost.done, updateItemHandler)
  .on(getById.done, (state, { result }) => result?.data.body.content || [])
  .reset(reset);

const spam = combine({
  preloaders,
  list,
  pagination,
  filters,
  prefetchedPage,
  prefetchedPageRequest,
  shouldPrefetchNextPageFast,
  setPagination,
});

export default spam;
